import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import payslipService from '../../../services/payslipService';

const PayslipGeneratorPanel = () => {
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [generatedPayslips, setGeneratedPayslips] = useState([]);
  const [payslipStats, setPayslipStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    loadApprovedPayrollRuns();
  }, []);

  useEffect(() => {
    if (selectedRun) {
      loadPayslipsForRun(selectedRun);
    }
  }, [selectedRun]);

  const loadApprovedPayrollRuns = async () => {
    try {
      setLoading(true);
      const runs = await payslipService?.getApprovedPayrollRuns();
      setPayrollRuns(runs);
    } catch (err) {
      setError(err?.message || 'Failed to load payroll runs');
    } finally {
      setLoading(false);
    }
  };

  const loadPayslipsForRun = async (runId) => {
    try {
      setLoading(true);
      const payslips = await payslipService?.getPayslipsByPayrollRun(runId);
      setGeneratedPayslips(payslips);

      const stats = await payslipService?.getPayslipStatistics(runId);
      setPayslipStats(stats);
    } catch (err) {
      setError(err?.message || 'Failed to load payslips');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePayslips = async () => {
    if (!selectedRun) {
      setError('Please select a payroll run');
      return;
    }

    try {
      setGenerating(true);
      setError('');

      const result = await payslipService?.generatePayslipsFromPayrollRun(
        selectedRun,
        'system@example.com'
      );

      if (result?.success) {
        await loadPayslipsForRun(selectedRun);
        await loadApprovedPayrollRuns();
      }
    } catch (err) {
      setError(err?.message || 'Failed to generate payslips');
    } finally {
      setGenerating(false);
    }
  };

  const handleViewDetails = async (payslipId) => {
    try {
      const details = await payslipService?.getPayslipById(payslipId);
      setSelectedPayslip(details);
      setShowDetailModal(true);
    } catch (err) {
      setError(err?.message || 'Failed to load payslip details');
    }
  };

  const handleDownloadPayslip = (payslip) => {
    console.log('Downloading payslip:', payslip?.payslipNumber);
    // PDF generation would be implemented here
  };

  const handleBulkDownload = () => {
    console.log('Downloading all payslips');
    // Bulk PDF generation would be implemented here
  };

  const handleEmailPayslip = async (payslipId) => {
    try {
      await payslipService?.markPayslipAsSent(payslipId);
      await loadPayslipsForRun(selectedRun);
      console.log('Payslip emailed successfully');
    } catch (err) {
      setError(err?.message || 'Failed to email payslip');
    }
  };

  const formatCurrency = (amount) => {
    return `K ${Number(amount || 0)?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-PG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const selectedRunData = payrollRuns?.find(run => run?.id === selectedRun);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Digital Payslip Generator</h3>
          <p className="text-sm text-gray-600 mt-1">Generate audit-ready payslips in PNG Kina format with detailed breakdown</p>
        </div>
        <div className="flex gap-2">
          {generatedPayslips?.length > 0 && (
            <Button
              onClick={handleBulkDownload}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Icon name="Download" className="h-4 w-4" />
              Download All
            </Button>
          )}
          <Button
            onClick={handleGeneratePayslips}
            disabled={generating || !selectedRun || selectedRunData?.hasPayslips}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-lg font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Generating...
              </>
            ) : (
              <>
                <Icon name="FileText" className="h-5 w-5" />
                Generate Payslips
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <Icon name="AlertCircle" className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Payroll Run Selection */}
      <div className="bg-gray-50 rounded-lg p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Select Approved Payroll Run</label>
        <select
          value={selectedRun || ''}
          onChange={(e) => setSelectedRun(e?.target?.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        >
          <option value="">Choose a payroll run</option>
          {payrollRuns?.map((run) => (
            <option key={run?.id} value={run?.id}>
              {formatDate(run?.payPeriodStart)} - {formatDate(run?.payPeriodEnd)} | 
              Payment: {formatDate(run?.paymentDate)} | 
              Total: {formatCurrency(run?.totalNet)} |
              {run?.hasPayslips ? ' ✓ Payslips Generated' : ' Pending'}
            </option>
          ))}
        </select>
        {selectedRunData && (
          <div className="mt-3 grid grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Gross Pay:</span>
              <p className="font-semibold text-gray-900">{formatCurrency(selectedRunData?.totalGross)}</p>
            </div>
            <div>
              <span className="text-gray-600">Net Pay:</span>
              <p className="font-semibold text-emerald-600">{formatCurrency(selectedRunData?.totalNet)}</p>
            </div>
            <div>
              <span className="text-gray-600">SWT:</span>
              <p className="font-semibold text-gray-900">{formatCurrency(selectedRunData?.totalSwt)}</p>
            </div>
            <div>
              <span className="text-gray-600">NASFUND:</span>
              <p className="font-semibold text-gray-900">{formatCurrency(selectedRunData?.totalNasfund)}</p>
            </div>
          </div>
        )}
      </div>

      {/* Payslip Statistics */}
      {payslipStats && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Payslips</p>
                <p className="text-2xl font-bold text-gray-900">{payslipStats?.total}</p>
              </div>
              <Icon name="FileText" className="h-8 w-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Generated</p>
                <p className="text-2xl font-bold text-blue-600">{payslipStats?.generated}</p>
              </div>
              <Icon name="CheckCircle" className="h-8 w-8 text-blue-400" />
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Sent</p>
                <p className="text-2xl font-bold text-emerald-600">{payslipStats?.sent}</p>
              </div>
              <Icon name="Mail" className="h-8 w-8 text-emerald-400" />
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Failed</p>
                <p className="text-2xl font-bold text-red-600">{payslipStats?.failed}</p>
              </div>
              <Icon name="XCircle" className="h-8 w-8 text-red-400" />
            </div>
          </div>
        </div>
      )}

      {/* Generated Payslips */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading payslips...</p>
        </div>
      ) : generatedPayslips?.length > 0 ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-gray-900">Generated Payslips ({generatedPayslips?.length})</h4>
            <span className="text-sm text-gray-600">
              Generated: {formatDate(generatedPayslips?.[0]?.generatedAt)}
            </span>
          </div>

          {generatedPayslips?.map((payslip) => (
            <div key={payslip?.id} className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
              {/* Payslip Header */}
              <div className="flex items-start justify-between mb-6 pb-4 border-b-2 border-gray-200">
                <div>
                  <h5 className="text-xl font-bold text-gray-900">{payslip?.employee?.fullName}</h5>
                  <p className="text-sm text-gray-600">Employee No: {payslip?.employee?.employeeNumber}</p>
                  <p className="text-sm text-gray-600">Department: {payslip?.employee?.department}</p>
                  <p className="text-sm text-gray-600">Position: {payslip?.employee?.position}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-600">Payslip Number</p>
                  <p className="text-lg font-bold text-emerald-600">{payslip?.payslipNumber}</p>
                  <p className="text-sm text-gray-600 mt-1">Pay Period</p>
                  <p className="text-sm font-semibold">{formatDate(payslip?.payPeriod?.start)} - {formatDate(payslip?.payPeriod?.end)}</p>
                  <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-medium ${
                    payslip?.payslipStatus === 'sent' ? 'bg-emerald-100 text-emerald-700' :
                    payslip?.payslipStatus === 'generated'? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {payslip?.payslipStatus?.toUpperCase()}
                  </span>
                </div>
              </div>

              {/* PNG Kina Breakdown */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                {/* Earnings Section */}
                <div className="bg-emerald-50 rounded-lg p-4">
                  <h6 className="font-semibold text-emerald-900 mb-3 flex items-center gap-2">
                    <Icon name="TrendingUp" className="h-5 w-5" />
                    Earnings (PNG Kina)
                  </h6>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Base Pay:</span>
                      <span className="font-semibold text-gray-900">{formatCurrency(payslip?.earnings?.basePay)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Overtime Pay:</span>
                      <span className="font-semibold text-gray-900">{formatCurrency(payslip?.earnings?.overtimePay)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Allowances:</span>
                      <span className="font-semibold text-gray-900">{formatCurrency(payslip?.earnings?.allowances)}</span>
                    </div>
                    <div className="flex justify-between text-sm pt-2 border-t-2 border-emerald-200">
                      <span className="font-bold text-emerald-900">Gross Pay:</span>
                      <span className="font-bold text-emerald-900">{formatCurrency(payslip?.earnings?.grossPay)}</span>
                    </div>
                  </div>
                </div>

                {/* Deductions Section */}
                <div className="bg-red-50 rounded-lg p-4">
                  <h6 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
                    <Icon name="TrendingDown" className="h-5 w-5" />
                    Deductions (PNG Kina)
                  </h6>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">NASFUND (Employee):</span>
                      <span className="font-semibold text-gray-900">{formatCurrency(payslip?.deductions?.employeeSuper)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">SWT (Tax):</span>
                      <span className="font-semibold text-gray-900">{formatCurrency(payslip?.deductions?.swtAmount)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Other Deductions:</span>
                      <span className="font-semibold text-gray-900">{formatCurrency(payslip?.deductions?.otherDeductions)}</span>
                    </div>
                    <div className="flex justify-between text-sm pt-2 border-t-2 border-red-200">
                      <span className="font-bold text-red-900">Total Deductions:</span>
                      <span className="font-bold text-red-900">{formatCurrency(payslip?.deductions?.totalDeductions)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Net Pay */}
              <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-emerald-100 text-sm font-medium">Net Pay (Take Home)</p>
                    <p className="text-white text-3xl font-bold">{formatCurrency(payslip?.netPay)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-emerald-100 text-sm">Payment Date</p>
                    <p className="text-white text-lg font-semibold">{formatDate(payslip?.payPeriod?.paymentDate)}</p>
                  </div>
                </div>
              </div>

              {/* Hours Worked */}
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">Regular Hours: <span className="font-semibold">{payslip?.hours?.regularHours}</span></span>
                  <span className="text-gray-700">Overtime Hours: <span className="font-semibold">{payslip?.hours?.overtimeHours}</span></span>
                  <span className="text-gray-700">Total Hours: <span className="font-bold">{payslip?.hours?.totalHours}</span></span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  onClick={() => handleViewDetails(payslip?.id)}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2"
                >
                  <Icon name="Eye" className="h-4 w-4" />
                  View Details
                </Button>
                <Button
                  onClick={() => handleDownloadPayslip(payslip)}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2"
                >
                  <Icon name="Download" className="h-4 w-4" />
                  Download PDF
                </Button>
                <Button
                  onClick={() => handleEmailPayslip(payslip?.id)}
                  disabled={payslip?.payslipStatus === 'sent'}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="Mail" className="h-4 w-4" />
                  {payslip?.payslipStatus === 'sent' ? 'Sent' : 'Email to Employee'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <Icon name="FileText" className="h-16 w-16 mx-auto mb-4 text-gray-400" />
          <p className="text-lg font-medium">No payslips generated yet</p>
          <p className="text-sm mt-1">
            {selectedRun ? 'Click "Generate Payslips" to create payslips for this payroll run' : 'Select a payroll run to get started'}
          </p>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedPayslip && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Payslip Details - {selectedPayslip?.payslipNumber}</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Icon name="X" className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Employee Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Employee Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Full Name:</span>
                    <p className="font-semibold">{selectedPayslip?.employee?.fullName}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Employee Number:</span>
                    <p className="font-semibold">{selectedPayslip?.employee?.employeeNumber}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Department:</span>
                    <p className="font-semibold">{selectedPayslip?.employee?.department}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Position:</span>
                    <p className="font-semibold">{selectedPayslip?.employee?.position}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Email:</span>
                    <p className="font-semibold">{selectedPayslip?.employee?.email}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Phone:</span>
                    <p className="font-semibold">{selectedPayslip?.employee?.phone || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Complete Breakdown */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-emerald-50 rounded-lg p-4">
                  <h4 className="font-semibold text-emerald-900 mb-3">Complete Earnings Breakdown</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Base Pay:</span>
                      <span className="font-semibold">{formatCurrency(selectedPayslip?.earnings?.basePay)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Overtime Pay:</span>
                      <span className="font-semibold">{formatCurrency(selectedPayslip?.earnings?.overtimePay)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Allowances:</span>
                      <span className="font-semibold">{formatCurrency(selectedPayslip?.earnings?.allowances)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t-2 border-emerald-200 font-bold">
                      <span>Gross Pay:</span>
                      <span>{formatCurrency(selectedPayslip?.earnings?.grossPay)}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 rounded-lg p-4">
                  <h4 className="font-semibold text-red-900 mb-3">Complete Deductions Breakdown</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>NASFUND (Employee 6%):</span>
                      <span className="font-semibold">{formatCurrency(selectedPayslip?.deductions?.employeeSuper)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>SWT (Tax):</span>
                      <span className="font-semibold">{formatCurrency(selectedPayslip?.deductions?.swtAmount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Other Deductions:</span>
                      <span className="font-semibold">{formatCurrency(selectedPayslip?.deductions?.otherDeductions)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t-2 border-red-200 font-bold">
                      <span>Total Deductions:</span>
                      <span>{formatCurrency(selectedPayslip?.deductions?.totalDeductions)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Employer Contributions */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-3">Employer Contributions (Not deducted from employee)</h4>
                <div className="text-sm">
                  <div className="flex justify-between">
                    <span>NASFUND (Employer 8.4%):</span>
                    <span className="font-semibold">{formatCurrency(selectedPayslip?.deductions?.employerSuper)}</span>
                  </div>
                </div>
              </div>

              {/* Final Net Pay */}
              <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-lg p-6 text-center">
                <p className="text-emerald-100 text-sm font-medium mb-2">Net Pay (Take Home Amount)</p>
                <p className="text-white text-4xl font-bold">{formatCurrency(selectedPayslip?.netPay)}</p>
                <p className="text-emerald-100 text-sm mt-3">Payment Date: {formatDate(selectedPayslip?.payPeriod?.paymentDate)}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayslipGeneratorPanel;